import { APP_INITIALIZER, ApplicationConfig, inject, provideBrowserGlobalErrorListeners, provideZoneChangeDetection } from '@angular/core';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { provideClientHydration, withEventReplay } from '@angular/platform-browser';
import { catchError, firstValueFrom, of, switchMap } from 'rxjs';

import { routes } from './app.routes';
import { authInterceptor } from './shared/interceptors/auth.interceptor';
import { AuthService } from './shared/services/auth.service';
import { CartService } from './shared/services/cart.service';
import { PAYMENT_PROVIDER } from './shared/payments/payment-provider.token';
import { PaymentProvider } from './shared/payments/payment-provider.interface';
import { StubPaymentProvider } from './shared/payments/stub-payment.provider';
import { OnvoPaymentProvider } from './shared/payments/onvo-payment.provider';
import { environment } from '../environments/environment';

/**
 * Elige la pasarela según la config de entorno. Extraída (y exportada) para testearla
 * sin mockear `environment`. DEBE invocarse en contexto de inyección: el branch 'onvo'
 * hace `inject(ApiService)` dentro de `OnvoPaymentProvider`. La publishable key es pública
 * (va al bundle); la secret NUNCA llega al front.
 */
export function createPaymentProvider(
  provider: 'stub' | 'onvo',
  onvoPublishableKey: string,
): PaymentProvider {
  if (provider === 'onvo') {
    return new OnvoPaymentProvider(onvoPublishableKey);
  }
  return new StubPaymentProvider();
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withInMemoryScrolling({ scrollPositionRestoration: 'top' })),
    provideClientHydration(withEventReplay()),
    provideHttpClient(withInterceptors([authInterceptor]), withFetch()),
    {
      provide: APP_INITIALIZER,
      useFactory: () => {
        const auth = inject(AuthService);
        const cart = inject(CartService);
        return () =>
          firstValueFrom(
            auth.initAuth().pipe(
              catchError(() => of(null)),
              switchMap(() => cart.getCart().pipe(catchError(() => of(null)))),
            ),
          );
      },
      multi: true,
    },
    {
      // Pasarela de pago seleccionada por `environment.payments.provider`.
      // 'onvo' (Pasarela · 5) instancia el provider real contra los endpoints
      // token-scoped; 'stub' (dev/tests/prod hasta validar E2E) no cobra.
      provide: PAYMENT_PROVIDER,
      useFactory: () =>
        createPaymentProvider(
          environment.payments.provider,
          environment.payments.onvoPublishableKey,
        ),
    },
  ]
};
